/**
 * УДЕРЖАНИЕ ИТОГА СПОСОБНОСТИ НА ПОЛЕ, пока идет ее анимация (см.
 * scenes/playAbilityCastEffects): экшен способности меняет состояние сразу, а
 * сердце соблазна или бомба гоблина долетают до цели через секунду. Без
 * удержания соблазненный отряд перекрашивался бы еще до вылета сердца, а
 * строение рушилось бы раньше, чем в него попала бомба.
 *
 * Удержание — правка состояния ТОЛЬКО ДЛЯ ОТРИСОВКИ ПОЛЯ: сцена рисует поле по
 * состоянию, к которому применены все удержания (GameScene.handleStoreUpdate),
 * а стор, правила, клики и сеть живут по настоящему. Каждое удержание
 * возвращает на поле прежний вид своих сущностей (владельца отряда-цели,
 * строение ячейки, список ловушек, запись о броске) — и только их.
 *
 * Удержания применяются от новых к старым: если за время анимации ту же
 * сущность задело еще одно действие, на поле остается вид до первого из них,
 * пока не откроется его итог.
 *
 * Отдельный маленький источник с подпиской, а не redux: это состояние
 * анимации, а не партии (как и shotFlightTracker). Футер по нему придерживает
 * формулу броска, пока итог не открылся на поле (см. useIsAbilityResultHeld).
 */
const holds = new Map();
const listeners = new Set();

const notify = () => {
  [...listeners].forEach((listener) => listener());
};

/**
 * Придержать итог
 * @param {string} key — уникальный ключ удержания
 * @param {Object} hold
 * @param {function(Object): Object} hold.patch — состояние → состояние для отрисовки
 * @param {string[]} [hold.unitIds] — отряды, чей итог придержан (для футера)
 */
export const holdAbilityResult = (key, {patch, unitIds = []}) => {
  if (!key || typeof patch !== 'function') {
    return;
  }
  holds.set(key, {patch, unitIds});
  notify();
};

/**
 * Открыть итог
 * @returns {boolean} было ли такое удержание
 */
export const releaseAbilityResult = (key) => {
  const isDeleted = holds.delete(key);
  if (isDeleted) {
    notify();
  }
  return isDeleted;
};

/** Действует ли еще это удержание: разбор сцены снимает все */
export const checkIfAbilityResultHeldByKey = (key) => holds.has(key);

/** Есть ли удержания вообще */
export const checkIfAnyAbilityResultHeld = () => holds.size > 0;

/** Придержан ли итог способности этого отряда (применившего или цели) */
export const checkIfAbilityResultHeldForUnit = (unitId) => Boolean(unitId)
  && [...holds.values()].some((hold) => hold.unitIds.includes(unitId));

export const subscribeToAbilityResultHolds = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Состояние для отрисовки поля: с прежним видом придержанных сущностей. Без
 * удержаний — то же самое состояние (тот же объект)
 */
export const applyAbilityResultHolds = (state) => {
  if (!state || holds.size === 0) {
    return state;
  }
  return [...holds.values()].reverse().reduce((nextState, hold) => {
    try {
      return hold.patch(nextState) || nextState;
    } catch (error) {
      // Удержание — украшение: его сбой не должен ронять отрисовку поля
      console.warn('[AbilityResultHolds] не удалось применить удержание', error);
      return nextState;
    }
  }, state);
};

/** Забыть все (разбор сцены, тесты) */
export const resetAbilityResultHolds = () => {
  if (holds.size === 0) {
    return;
  }
  holds.clear();
  notify();
};

const patchSharedState = (state, patchShared) => ({...state, shared: patchShared(state.shared)});

const patchGameState = (shared, patchGame) => ({...shared, gameState: patchGame(shared.gameState)});

// Значение по ключу: undefined — ключа не было, его и не должно быть
const withKeyValue = (map, key, value) => {
  const next = {...(map || {})};
  if (value === undefined) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next;
};

// Оставить у отряда в списке записей не больше count первых: новые — итог,
// который еще не открыт
const keepFirstUnitEntries = (entries, unitId, count) => {
  let seen = 0;
  return (entries || []).filter((entry) => {
    if (entry?.unitId !== unitId) {
      return true;
    }
    seen += 1;
    return seen <= count;
  });
};

const countUnitEntries = (entries, unitId) => (entries || []).filter((entry) => entry?.unitId === unitId).length;

/**
 * Что запомнить ДО экшена соблазна, чтобы придержать его итог
 * @param {Object} prevState — состояние до экшена
 */
export const captureSeductionHold = (prevState, {unitId, targetUnitId}) => {
  const gameState = prevState?.shared?.gameState;
  const targetBefore = (gameState?.units || []).find((unit) => unit.id === targetUnitId);
  if (!targetBefore) {
    return null;
  }
  return {
    unitIds: [unitId, targetUnitId],
    patch: (state) => patchSharedState(state, (shared) => patchGameState(shared, (game) => ({
      ...game,
      units: (game.units || []).map((unit) => (unit.id === targetUnitId
        ? {...unit, ownerId: targetBefore.ownerId, capturedFromOwnerId: targetBefore.capturedFromOwnerId}
        : unit)),
      succubesSeductionActions: keepFirstUnitEntries(
        game.succubesSeductionActions, unitId, countUnitEntries(gameState.succubesSeductionActions, unitId)
      ),
    }))),
  };
};

/**
 * Отряды, погибшие от голода, стоят на поле, пока их гибель не покажут (см.
 * scenes/playUnitStarvationEffects). Ячейку, где уже стоит другой отряд, не занимают
 * @param {Object[]} starvedUnits — отряды в том виде, какими они стояли на поле
 */
export const captureStarvedUnitsHold = (starvedUnits) => ({
  patch: (state) => patchSharedState(state, (shared) => patchGameState(shared, (game) => {
    const units = game.units || [];
    const occupied = new Set(units.flatMap((unit) => [unit.id, unit.hexKey]));
    return {
      ...game,
      units: [...units, ...starvedUnits.filter((unit) => !occupied.has(unit.id) && !occupied.has(unit.hexKey))],
    };
  })),
});

/**
 * Что запомнить ДО экшена улучшения отряда: на поле он остается прежнего типа и
 * размера (улучшение режет размер по новому лимиту), пока шевроны его не закроют
 */
export const captureUnitUpgradeHold = (prevState, {unitId}) => {
  const unitBefore = (prevState?.shared?.gameState?.units || []).find((unit) => unit.id === unitId);
  if (!unitBefore) {
    return null;
  }
  const {type, maxSize, size} = unitBefore;
  return {
    unitIds: [unitId],
    patch: (state) => patchSharedState(state, (shared) => patchGameState(shared, (game) => ({
      ...game,
      units: (game.units || []).map((unit) => (unit.id === unitId ? {...unit, type, maxSize, size} : unit)),
    }))),
  };
};

/**
 * Передача размера от квартала отряду: докупка уже в списке (маркер с квартала
 * ушел сразу, и кварталом не купить второй раз), но «+N» на отряде появится, когда
 * долетит шар: запись только помечена, и бейдж ее не считает (см.
 * filterArrivedRecruitmentActions). Запись опознается по кварталу: с него докупка одна
 */
export const captureUnitSizeTransferHold = ({quarterHexKey}) => ({
  patch: (state) => patchSharedState(state, (shared) => patchGameState(shared, (game) => ({
    ...game,
    pendingRecruitmentActions: (game.pendingRecruitmentActions || []).map((action) => (
      action.quarterHexKey === quarterHexKey ? {...action, isTransferInFlight: true} : action
    )),
  }))),
});

/** Записи найма, чей итог уже виден на отряде: без тех, что еще летят к нему */
export const filterArrivedRecruitmentActions = (actions) => (actions || []).filter((action) => !action.isTransferInFlight);

/**
 * Что запомнить ДО экшена повреждения строения гоблином: жетон ячейки,
 * спецстроение, урон по ней и — если строение рушится — города целиком
 * (граница владений меняется вместе с ним)
 */
export const captureBuildingDamageHold = (prevState, {unitId, hexKey}) => {
  const shared = prevState?.shared;
  const gameState = shared?.gameState;
  if (!shared || !gameState) {
    return null;
  }
  const hexToken = shared.hexes?.[hexKey];
  const specialBuilding = gameState.specialBuildings?.[hexKey];
  const damage = gameState.buildingsDamage?.[hexKey];
  const {citiesCache} = gameState;
  const actionCount = countUnitEntries(gameState.goblinsAbilityActions, unitId);
  return {
    unitIds: [unitId],
    patch: (state) => patchSharedState(state, (nextShared) => ({
      ...patchGameState(nextShared, (game) => ({
        ...game,
        specialBuildings: withKeyValue(game.specialBuildings, hexKey, specialBuilding),
        buildingsDamage: withKeyValue(game.buildingsDamage, hexKey, damage),
        // Города меняются, только когда строение разрушено
        citiesCache: game.specialBuildings?.[hexKey] === specialBuilding && nextShared.hexes?.[hexKey] === hexToken
          ? game.citiesCache
          : citiesCache,
        goblinsAbilityActions: keepFirstUnitEntries(game.goblinsAbilityActions, unitId, actionCount),
      })),
      hexes: withKeyValue(nextShared.hexes, hexKey, hexToken),
    })),
  };
};

/**
 * Что запомнить ДО экшена установки ловушки: новая ловушка (ее id известен
 * заранее) и запись о броске появятся на поле только на попадании
 */
export const captureTrapSetupHold = (prevState, {unitId, trapId}) => {
  const gameState = prevState?.shared?.gameState;
  if (!gameState) {
    return null;
  }
  const actionCount = countUnitEntries(gameState.goblinsAbilityActions, unitId);
  return {
    unitIds: [unitId],
    patch: (state) => patchSharedState(state, (shared) => patchGameState(shared, (game) => ({
      ...game,
      goblinsTraps: (game.goblinsTraps || []).filter((trap) => trap.id !== trapId),
      goblinsAbilityActions: keepFirstUnitEntries(game.goblinsAbilityActions, unitId, actionCount),
    }))),
  };
};
